<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({ under_1h: 0, under_24h: 0, over_24h: 0, total: 0 }),
  },
  loading: { type: Boolean, default: false },
});

const { t } = useI18n();

const tiers = computed(() => {
  const total = props.data.total || 1;
  return [
    {
      key: 'under_1h',
      label: t('CEO_DASHBOARD.RESOLUTION_SPEED.UNDER_1H'),
      count: props.data.under_1h || 0,
      pct: ((props.data.under_1h || 0) / total * 100).toFixed(1),
      color: 'bg-n-teal-9',
      textColor: 'text-n-teal-11',
    },
    {
      key: 'under_24h',
      label: t('CEO_DASHBOARD.RESOLUTION_SPEED.UNDER_24H'),
      count: props.data.under_24h || 0,
      pct: ((props.data.under_24h || 0) / total * 100).toFixed(1),
      color: 'bg-n-amber-9',
      textColor: 'text-n-amber-11',
    },
    {
      key: 'over_24h',
      label: t('CEO_DASHBOARD.RESOLUTION_SPEED.OVER_24H'),
      count: props.data.over_24h || 0,
      pct: ((props.data.over_24h || 0) / total * 100).toFixed(1),
      color: 'bg-n-ruby-9',
      textColor: 'text-n-ruby-11',
    },
  ];
});
</script>

<template>
  <div class="flex flex-col rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2 px-6 py-5">
    <h3 class="mb-4 text-sm font-medium text-n-slate-11 uppercase tracking-wider">
      {{ t('CEO_DASHBOARD.RESOLUTION_SPEED.HEADER') }}
    </h3>
    <div v-if="loading" class="flex items-center justify-center py-8">
      <Spinner />
    </div>
    <template v-else>
      <!-- Stacked progress bar -->
      <div class="flex w-full h-4 rounded-full overflow-hidden bg-n-alpha-1 mb-4">
        <div
          v-for="tier in tiers"
          :key="tier.key"
          :class="tier.color"
          :style="{ width: tier.pct + '%' }"
          class="h-full transition-all duration-300"
        />
      </div>

      <!-- Legend -->
      <div class="grid grid-cols-3 gap-4">
        <div v-for="tier in tiers" :key="tier.key" class="flex flex-col items-center">
          <span class="text-xl font-bold" :class="tier.textColor">
            {{ tier.count.toLocaleString() }}
          </span>
          <span class="text-xs text-n-slate-10 text-center">{{ tier.label }}</span>
          <span class="text-xs font-medium" :class="tier.textColor">{{ tier.pct }}%</span>
        </div>
      </div>
    </template>
  </div>
</template>
